import { useEffect, useMemo, useRef, useState } from "react";
import { resolveAmountLimits } from "../api/limits";
import type { FundingMethod } from "../utils/catalog";
import {
  corridorsForFundingMethod,
  fundingLimitsOptionsKey,
  mergeListedBounds,
  mergeRemainingBounds,
  type FundingAmountBound,
  type FundingLimitsSku,
  type FundingMethodDetails,
} from "../utils/fundingLimits";

export type { FundingMethodDetails };

/**
 * Per-method fee (pre-quote `customerFee`) and min/max send bounds
 * (`POST /widget/limits/resolve`) for the funding-method picker.
 * D20: N resolves per listed method; D21: merge remaining bounds.
 */
export const useFundingMethodLimits = (
  methods: FundingMethod[],
  queriesForMethod: (method: FundingMethod) => FundingLimitsSku[],
  originCountryCode: string,
  destinationCountryCode: string,
  sendCurrency: string
): {
  details: Partial<Record<FundingMethod, FundingMethodDetails>>;
  loading: boolean;
} => {
  const [bounds, setBounds] = useState<
    Partial<Record<FundingMethod, FundingAmountBound>>
  >({});
  const [loading, setLoading] = useState(false);
  const stickyCorridors = useRef<Partial<Record<FundingMethod, string[]>>>({});

  const optionsKey = fundingLimitsOptionsKey(methods, queriesForMethod);

  useEffect(() => {
    let cancelled = false;

    if (methods.length === 0) {
      setBounds({});
      setLoading(false);
      return;
    }

    const listed = new Set(methods);
    for (const key of Object.keys(stickyCorridors.current) as FundingMethod[]) {
      if (!listed.has(key)) delete stickyCorridors.current[key];
    }

    const load = async () => {
      setLoading(true);
      const next: Partial<Record<FundingMethod, FundingAmountBound>> = {};

      await Promise.all(
        methods.map(async (method) => {
          const pageQueries = queriesForMethod(method);
          const pinned = pageQueries[0];
          const corridors = corridorsForFundingMethod(method, {
            options: pageQueries,
            stickyCorridors: stickyCorridors.current[method],
          });
          if (corridors.length === 0) return;
          stickyCorridors.current[method] = corridors;

          const responses = await Promise.all(
            corridors.map(async (corridorCode) => {
              try {
                const bound = await resolveAmountLimits({
                  corridorCode,
                  originCountryCode,
                  destinationCountryCode,
                  payoutMethod: pinned?.payoutMethod || undefined,
                  payerCode: pinned?.payerCode ?? undefined,
                  fundingMethod: pinned?.fundingMethod || method,
                  sendCurrency,
                });
                return {
                  minAmount: bound.minSend,
                  maxAmount: bound.maxSend,
                };
              } catch {
                return undefined;
              }
            })
          );
          next[method] = mergeRemainingBounds(responses);
        })
      );

      if (!cancelled) {
        setBounds((prev) => mergeListedBounds(prev, next, methods));
        setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // optionsKey captures method + query identities; queriesForMethod is
    // stable for a given harvest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsKey, originCountryCode, destinationCountryCode, sendCurrency]);

  const details = useMemo(() => {
    const result: Partial<Record<FundingMethod, FundingMethodDetails>> = {};
    for (const method of methods) {
      const queries = queriesForMethod(method);
      const fee =
        queries.find((sku) => sku.customerFee != null)?.customerFee ?? null;
      result[method] = {
        fee,
        minAmount: bounds[method]?.minAmount ?? null,
        maxAmount: bounds[method]?.maxAmount ?? null,
      };
    }
    return result;
  }, [bounds, methods, queriesForMethod]);

  return { details, loading };
};
